import { z } from "zod";

import type {
  AssetRequirement,
  AssetRequirementKind,
  ManifestFragment
} from "@/app/integration/contracts";
import {
  hasUniqueValues,
  kebabCaseIdentifierSchema,
  levelIdSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";

export function classifyPublicResourcePath(
  path: string
): AssetRequirementKind | undefined {
  if (path.startsWith("/audio/")) {
    return "audio";
  }
  if (path.startsWith("/assets/")) {
    return "image";
  }
  return undefined;
}

export const assetRequirementSchema = z
  .strictObject({
    path: localResourceReferenceSchema.refine(
      (path) => classifyPublicResourcePath(path) !== undefined,
      { message: "Expected an absolute /assets/ or /audio/ path" }
    ),
    kind: z.enum(["audio", "image"]),
    levelIds: z
      .array(levelIdSchema)
      .min(1)
      .max(90)
      .refine((levelIds) => hasUniqueValues(levelIds), {
        message: "Requirement level IDs must be unique"
      })
  })
  .superRefine((requirement, context) => {
    const expectedKind = classifyPublicResourcePath(requirement.path);
    if (expectedKind !== undefined && requirement.kind !== expectedKind) {
      context.addIssue({
        code: "custom",
        path: ["kind"],
        message:
          "Expected " + expectedKind + " for path " + requirement.path
      });
    }
  }) satisfies z.ZodType<AssetRequirement>;

export const manifestFragmentSchema = z
  .strictObject({
    owner: kebabCaseIdentifierSchema,
    requirements: z
      .array(assetRequirementSchema)
      .max(500)
      .refine(
        (requirements) =>
          hasUniqueValues(
            requirements.map(({ path }) => path)
          ),
        { message: "Manifest requirement paths must be unique per owner" }
      )
  }) satisfies z.ZodType<ManifestFragment>;
